/* Eksplisitt gruppeflyt for fordelte betalinger og interne overfoeringer.
 * Regnskapsvalidering og bokfoering eies av backend; denne flaten bygger bare
 * et godkjenningsforslag som bokfores samlet ved avslutning av bankutskriften.
 */
(function registerBankMatchGroupModal(scope) {
  const safeArray = (value) => Array.isArray(value) ? value : [];
  const number = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;
  const money = (value) => Math.abs(number(value)).toLocaleString(PlummeI18n.intlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr';
  const rawStatement = (statement) => statement?._backend || statement || {};
  const rawLine = (line) => line?._backend || line || {};

  async function bookApprovedBankMatchGroup({ backend, line, onChanged }) {
    const groupId = String(line?._backend?.bankMatchGroupId || '');
    if (!groupId || !backend?.bookBankMatchGroup) return;
    try {
      await backend.bookBankMatchGroup(groupId);
      onChanged?.();
      toast(t('bank.group.toast.booked'), 'success');
    } catch (error) {
      toast(error?.message || t('bank.group.err.bookFailed'), 'error');
    }
  }

  const availableBankLines = (state) => safeArray(state?.bankStatements).flatMap((statement) => {
    const rawStmt = rawStatement(statement);
    if ((rawStmt.source || 'business') !== 'business' || rawStmt.bookedAt || rawStmt.reconciledAt || ['archived', 'booked', 'reconciled'].includes(rawStmt.status)) return [];
    return safeArray(rawStmt.lines).filter((line) => !line?.matchedEntryId && !line?.bankMatchGroupId && number(line?.amount) !== 0).map((line) => ({
      statementId: rawStmt.id,
      lineId: line.id,
      bankAccountId: rawStmt.bankAccountId || '',
      statementName: rawStmt.fileName || rawStmt.originalFileName || rawStmt.id,
      date: line.date || '',
      description: line.description || 'Banklinje',
      amount: number(line.amount),
    }));
  });

  const lineKey = (line) => `${line.statementId}:${line.lineId}`;

  function BankMatchGroupModal({ statement, initialLine, onClose, onChanged }) {
    const backend = scope.PlummeBackend;
    const state = backend?.appState || {};
    const lines = availableBankLines(state);
    const initialKey = initialLine && statement
      ? `${rawStatement(statement).id}:${rawLine(initialLine).id}`
      : '';
    const initialSource = lines.find((line) => lineKey(line) === initialKey) || lines[0] || null;
    const [type, setType] = useState('allocation');
    const [sourceKeys, setSourceKeys] = useState(initialSource ? [lineKey(initialSource)] : []);
    const [transferSourceKey, setTransferSourceKey] = useState(initialSource ? lineKey(initialSource) : '');
    const [transferTargetKey, setTransferTargetKey] = useState('');
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);

    const selectedLines = sourceKeys.map((key) => lines.find((line) => lineKey(line) === key)).filter(Boolean);
    const direction = selectedLines[0]?.amount < 0 ? 'outgoing' : 'incoming';
    const sameDirectionLines = lines.filter((line) => direction === 'outgoing' ? line.amount < 0 : line.amount > 0);
    const openItems = backend?.getBankAllocationOpenItems?.(direction) || [];
    const transferSource = lines.find((line) => lineKey(line) === transferSourceKey) || null;
    const transferCandidates = transferSource ? lines.filter((line) =>
      lineKey(line) !== transferSourceKey && line.bankAccountId !== transferSource.bankAccountId &&
      Math.sign(line.amount) !== Math.sign(transferSource.amount) &&
      line.date === transferSource.date &&
      Math.abs(Math.abs(line.amount) - Math.abs(transferSource.amount)) <= 0.005) : [];

    useEffect(() => {
      if (type !== 'allocation' || !selectedLines.length || rows.length) return;
      setRows(selectedLines.map((line, index) => ({
        id: `allocation-${index + 1}`,
        sourceKey: lineKey(line),
        targetKey: openItems[0] ? `${openItems[0].documentType}:${openItems[0].documentId}` : '',
        amount: String(Math.abs(line.amount)),
      })));
    }, [type, sourceKeys.join('|'), openItems.length]);

    useEffect(() => {
      if (!transferCandidates.some((line) => lineKey(line) === transferTargetKey)) {
        setTransferTargetKey(transferCandidates[0] ? lineKey(transferCandidates[0]) : '');
      }
    }, [transferSourceKey, transferCandidates.map(lineKey).join('|')]);

    const toggleSource = (key) => {
      const candidate = lines.find((line) => lineKey(line) === key);
      if (!candidate) return;
      const next = sourceKeys.includes(key) ? sourceKeys.filter((entry) => entry !== key) : [...sourceKeys, key];
      const nextLines = next.map((entry) => lines.find((line) => lineKey(line) === entry)).filter(Boolean);
      if (nextLines.some((line) => Math.sign(line.amount) !== Math.sign(candidate.amount))) {
        toast(t('bank.group.err.sameDirection'), 'error');
        return;
      }
      setSourceKeys(next);
      setRows((current) => current.filter((row) => next.includes(row.sourceKey)));
    };

    const patchRow = (id, patch) => setRows((current) => current.map((row) => row.id === id ? { ...row, ...patch } : row));
    const addRow = () => {
      const source = selectedLines[0];
      setRows((current) => [...current, {
        id: `allocation-${Date.now()}-${current.length}`,
        sourceKey: source ? lineKey(source) : '',
        targetKey: openItems[0] ? `${openItems[0].documentType}:${openItems[0].documentId}` : '',
        amount: '',
      }]);
    };

    const approve = async () => {
      try {
        setBusy(true);
        let group = null;
        if (type === 'internal-transfer') {
          const sourceLine = lines.find((line) => lineKey(line) === transferSourceKey);
          const targetLine = lines.find((line) => lineKey(line) === transferTargetKey);
          if (!sourceLine || !targetLine) throw new Error(t('bank.group.err.pickBoth'));
          group = await backend.approveBankMatchGroup({
            type: 'internal-transfer',
            description: `Intern overføring: ${sourceLine.description}`,
            bankLines: [sourceLine, targetLine].map(({ statementId, lineId }) => ({ statementId, lineId })),
          });
        } else {
          if (!selectedLines.length) throw new Error(t('bank.priv.pickOneLine'));
          const allocations = rows.map((row) => {
            const source = lines.find((line) => lineKey(line) === row.sourceKey);
            const separator = row.targetKey.indexOf(':');
            return {
              id: row.id,
              statementId: source?.statementId,
              lineId: source?.lineId,
              documentType: row.targetKey.slice(0, separator),
              documentId: row.targetKey.slice(separator + 1),
              amount: number(String(row.amount).replace(',', '.')),
            };
          });
          group = await backend.approveBankMatchGroup({
            type: 'allocation',
            description: 'Fordelt bankbetaling',
            bankLines: selectedLines.map(({ statementId, lineId }) => ({ statementId, lineId })),
            allocations,
          });
        }
        if (!group?.id || !backend.bookBankMatchGroup) throw new Error(t('bank.group.err.bookUnavailable'));
        await backend.bookBankMatchGroup(group.id);
        onChanged?.();
        toast(type === 'internal-transfer' ? t('bank.group.toast.transferApproved') : t('bank.group.toast.allocationApproved'), 'success');
        onClose();
      } catch (error) {
        toast(error?.message || t('bank.group.err.approveFailed'), 'error');
      } finally {
        setBusy(false);
      }
    };

    const lineOption = (line) => `${line.date} · ${line.description} · ${line.amount < 0 ? '−' : '+'}${money(line.amount)} · ${line.statementName}`;
    return <Modal open onClose={onClose} title={t('bank.group.title')} max={820}
      footer={<div className="row between" style={{ width: '100%' }}><Button variant="ghost" onClick={onClose}>{t('sharedText.ui.cancel')}</Button><Button icon="check" loading={busy} onClick={approve} data-help={t('helpTopics.bankMatching.body')} data-help-title={t('helpTopics.bankMatching.title')}>{t('bank.group.approve')}</Button></div>}>
      <div className="col gap-16">
        <div className="alert" style={{ padding: 14 }}><b>{t('bank.group.oneOperation')}</b><div className="t-sm muted" style={{ marginTop: 4 }}>{t('bank.group.oneOperationHelp')}</div></div>
        <Field label={t('bank.group.matchType')} help={t('helpTopics.bankMatching.body')} helpTitle={t('helpTopics.bankMatching.title')}><Select value={type} onChange={(event) => setType(event.target.value)}><option value="allocation">{t('bank.group.typeAllocation')}</option><option value="internal-transfer">{t('bank.group.typeTransfer')}</option></Select></Field>

        {type === 'internal-transfer' ? <React.Fragment>
          <Field label={t('bank.group.sourceLine')} help={t('helpTopics.internalTransfer.body')} helpTitle={t('helpTopics.internalTransfer.title')}><Select value={transferSourceKey} onChange={(event) => setTransferSourceKey(event.target.value)}><option value="">{t('sharedText.bwf.selectLine')}</option>{lines.map((line) => <option key={lineKey(line)} value={lineKey(line)}>{lineOption(line)}</option>)}</Select></Field>
          <Field label={t('bank.group.counterLine')} help={t('helpTopics.internalTransfer.body')} helpTitle={t('helpTopics.internalTransfer.title')}><Select value={transferTargetKey} onChange={(event) => setTransferTargetKey(event.target.value)}><option value="">{t('bank.group.pickOpposite')}</option>{transferCandidates.map((line) => <option key={lineKey(line)} value={lineKey(line)}>{lineOption(line)}</option>)}</Select><div className="t-xs muted" style={{ marginTop: 5 }}>{t('bank.group.transferRules')}</div></Field>
        </React.Fragment> : <React.Fragment>
          <div><div className="t-sm" style={{ fontWeight: 800, marginBottom: 8 }}>{t('sharedText.bg.bankLines')}</div><div className="col gap-6">{sameDirectionLines.map((line) => <label key={lineKey(line)} className="row gap-10" style={{ alignItems: 'center', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)' }}><input type="checkbox" checked={sourceKeys.includes(lineKey(line))} onChange={() => toggleSource(lineKey(line))}/><span className="t-sm" style={{ flex: 1 }}>{lineOption(line)}</span></label>)}</div></div>
          {!openItems.length ? <div className="alert warn">{t('bank.group.noOpenItems', { kind: direction === 'incoming' ? t('bank.group.customerInvoices') : t('bank.group.supplierInvoices') })}</div> : <div className="col gap-10">
            <div className="row between"><b>{t('sharedText.home.ana.ring')}</b><Button size="sm" variant="secondary" icon="plus" onClick={addRow}>{t('sharedText.pj.row.new')}</Button></div>
            {rows.map((row) => <div key={row.id} className="grid-3 gap-10" style={{ alignItems: 'end', padding: 12, border: '1px solid var(--border)', borderRadius: 'var(--r-ms)' }}>
              <Field label={t('sharedText.bank.bankLine')}><Select value={row.sourceKey} onChange={(event) => patchRow(row.id, { sourceKey: event.target.value })}>{selectedLines.map((line) => <option key={lineKey(line)} value={lineKey(line)}>{line.date} · {money(line.amount)}</option>)}</Select></Field>
              <Field label={t('sharedText.helpTopics.openItem.title')} help={t('helpTopics.openItem.body')} helpTitle={t('sharedText.helpTopics.openItem.title')}><Select value={row.targetKey} onChange={(event) => patchRow(row.id, { targetKey: event.target.value })}>{openItems.map((item) => <option key={`${item.documentType}:${item.documentId}`} value={`${item.documentType}:${item.documentId}`}>{t('bank.group.itemRest', { number: item.number, party: item.party, amount: money(item.remainingAmount) })}</option>)}</Select></Field>
              <div className="row gap-6" style={{ alignItems: 'end' }}><Field label={t('sharedText.clo.amount')}><Input inputMode="decimal" value={row.amount} onChange={(event) => patchRow(row.id, { amount: event.target.value })}/></Field><Button variant="ghost" icon="trash" title={t('bank.group.removeRow')} onClick={() => setRows((current) => current.filter((entry) => entry.id !== row.id))}/></div>
            </div>)}
          </div>}
        </React.Fragment>}
      </div>
    </Modal>;
  }

  Object.assign(scope, { BankMatchGroupModal, bookApprovedBankMatchGroup });
})(typeof window !== 'undefined' ? window : globalThis);
